/* eslint-disable react-hooks/exhaustive-deps */
import { useFnbReviewList } from '@/services/queries';
import { useConfigStore } from '@/stores';
import type { MenuItem, PoiContentSlide, PoiDetails, ReviewItem } from '@/types/shop';
import { generateMenuKey } from '@/utils/floor-guide';
import { getPoiDetailImageUrl } from '@/utils/poi/poiImage';
import { useMemo, useState } from 'react';

const useFnbDataModal = () => {
    const ui = useConfigStore((state) => state.config.ui);

    const [isOpen, setIsOpen] = useState(false);
    const [clickedFnbPoi, setClickedFnbPoi] = useState<PoiContentSlide | null>(null);

    const { data: fnbReviewList, isLoading } = useFnbReviewList(clickedFnbPoi?.data?.contents.cstrShopNo[ui.language] || '');

    const parseMenuList = (rawMenuList: string | undefined): MenuItem[] | null => {
        if (!rawMenuList) return null;

        try {
            const parsed = JSON.parse(rawMenuList);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return null;
        }
    };

    const menuListProps = useMemo((): MenuItem[] | null => {
        const rawMenuList = clickedFnbPoi?.data.contents.menuList?.['ko'];
        const menuList = parseMenuList(rawMenuList);

        if (!menuList || menuList.length === 0) return null;

        return menuList.map((menu: any) => ({
            id: menu.shopPrdcNo,
            name: menu?.[generateMenuKey(ui.language)],
            price: menu.prdcPrc,
        }));
    }, [clickedFnbPoi?.id]);

    const reviewListProps = useMemo((): ReviewItem[] => {
        return (
            fnbReviewList?.data?.reviewInfoList?.map((review) => ({
                id: review.prdcEvlNo || '',
                imageSrc: review.imgUrlList[0] || '',
                content: review.revwCont,
                reviewedDate: review.rgstDtm,
                rating: Number(review.prdcEvlScor),
                shopName: clickedFnbPoi?.name || '',
            })) || []
        );
    }, [fnbReviewList?.data?.reviewInfoList]);

    const poiDetailsProps = useMemo((): PoiDetails => {
        const managedContents = clickedFnbPoi?.data.managedContents;

        const imgUrlDetail = getPoiDetailImageUrl(managedContents, ui.language);

        const shopCntcContent = managedContents?.find((item) => item.field_key === 'shopCntc')?.content;
        const phoneNumber = shopCntcContent?.[ui.language] || shopCntcContent?.ko || '';

        const categoryName = clickedFnbPoi?.isDutyFreePoi ? clickedFnbPoi?.categoryName : clickedFnbPoi?.category?.name;

        return {
            address: clickedFnbPoi?.buildingName || '',
            category: (categoryName as string) || '',
            floorName: clickedFnbPoi?.floor || '',
            name: clickedFnbPoi?.name || '',
            phoneNumber,
            imageSrc: imgUrlDetail || '',
        };
    }, [clickedFnbPoi, ui.language]);

    return {
        isOpen,
        clickedFnbPoi,
        reviewListProps,
        menuListProps,
        poiDetailsProps,
        isLoading,
        setIsOpen,
        setClickedFnbPoi,
    };
};

export default useFnbDataModal;
